import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { Sparkles, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

export default function CategoryProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [draftName, setDraftName] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/profiles").then(({ data }) => setProfiles(data)).catch(() => toast.error("Failed to load profiles"));
  useEffect(() => { load(); }, []);

  const draftAI = async () => {
    if (!draftName.trim()) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/profiles/draft-ai", { category_name: draftName.trim() });
      const created = await api.post("/profiles", {
        category_name: data.category_name || draftName.trim(),
        schema_keywords: data.schema_keywords || "",
        additional_type: data.additional_type || "",
        services: data.services || [],
        palette: data.palette || [],
        heading_font: data.heading_font || "Plus Jakarta Sans",
        body_font: data.body_font || "Source Sans 3",
        trust_items: data.trust_items || [],
        why_choose_us: data.why_choose_us || [],
        faq_topics: data.faq_topics || [],
        keyword_phrases: data.keyword_phrases || [],
        image_library: [],
      });
      toast.success("AI draft created — review & save");
      navigate(`/profiles/${created.data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setDrafting(false); setShowDraft(false); setDraftName(""); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this category profile?")) return;
    await api.delete(`/profiles/${id}`); load(); toast.success("Deleted");
  };

  const primary = (p) => p.palette?.find((c) => c.role === "primary_button")?.hex || "#F06432";

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className={ui.pageHeader}>Category Profiles</h1>
          <p className={`${ui.muted} mt-1`}>The recipe each website is built from.</p>
        </div>
        <button data-testid="draft-ai-btn" onClick={() => setShowDraft(true)} className={ui.btnPrimary}><Sparkles className="w-4 h-4" /> Draft with AI</button>
      </div>

      {showDraft && (
        <div className={`${ui.card} p-6 mb-6`} data-testid="draft-ai-panel">
          <label className={ui.label}>New category name</label>
          <div className="flex gap-2">
            <input autoFocus data-testid="draft-name" className={ui.input} value={draftName} onChange={(e) => setDraftName(e.target.value)} placeholder="e.g. Garage Door Repair" onKeyDown={(e) => e.key === "Enter" && draftAI()} />
            <button data-testid="draft-generate" onClick={draftAI} disabled={drafting} className={ui.btnPrimary}>{drafting ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />} Draft</button>
            <button onClick={() => setShowDraft(false)} className={ui.btnGhost}>Cancel</button>
          </div>
          <p className="text-xs text-slate-500 mt-2">AI proposes services, palette, fonts, FAQs & more for you to review and edit.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {profiles.map((p) => (
          <div key={p.id} data-testid={`profile-card-${p.id}`} onClick={() => navigate(`/profiles/${p.id}`)}
            className={`${ui.card} p-5 cursor-pointer hover:border-indigo-500/40 transition-colors group`}>
            <div className="flex items-start justify-between">
              <h3 className={ui.cardTitle}>{p.category_name}</h3>
              <button onClick={(e) => del(p.id, e)} className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-500 mt-1">{p.services?.length} services · {p.variants?.join("/")}</p>
            <div className="flex gap-1.5 mt-4">
              {p.palette?.slice(0, 7).map((c, i) => <span key={i} className="w-6 h-6 rounded-md border border-slate-700" style={{ background: c.hex }} title={`${c.name} (${c.role})`} />)}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800">
              <span className="text-xs font-mono text-slate-500">{p.heading_font} / {p.body_font}</span>
              <span className="text-xs text-indigo-400 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
